import React, { useState } from "react";
import {
  TrendingUp,
  ChevronDown,
  CreditCard,
  Check,
  Pencil,
  Trash2,
} from "lucide-react";
import { toast } from "react-hot-toast";
import confetti from "canvas-confetti";
import {
  DEFAULT_UPSELL_SERVICES,
  UPSELL_WEBHOOK_URL,
} from "../../config/constants";
import { useCase } from "../../context/CaseContext";

const UpsellManager: React.FC = () => {
  const {
    upsellItems,
    setUpsellItems,
    savePartial,
    currentCaseId,
    clientName,
    contactEmail,
    user,
    hubspotIds,
    logAuditAction,
    deleteItem,
  } = useCase();

  const [showUpsell, setShowUpsell] = useState(false);
  const [newUpsellItem, setNewUpsellItem] = useState({
    name: "",
    description: "",
    quantity: "1",
    price: "",
    billingFrequency: "one-time",
  });
  const [upsellSuggestions, setUpsellSuggestions] = useState<any[]>([]);
  const [editingUpsellIndex, setEditingUpsellIndex] = useState<number | null>(
    null,
  );
  const [editingUpsellItem, setEditingUpsellItem] = useState<any>({});
  const [isSendingUpsell, setIsSendingUpsell] = useState(false);

  const parsePrice = (price: any) =>
    parseFloat(String(price).replace(/[^0-9.-]+/g, "")) || 0;

  const updateItem = (
    list: any[],
    setList: any,
    idx: number,
    item: any,
    saveKey: string,
  ) => {
    const u = [...list];
    u[idx] = item;
    setList(u);
    savePartial({ [saveKey]: u });
  };

  const handleUpsellSubmit = async () => {
    if (upsellItems.length === 0) return;

    setIsSendingUpsell(true);

    // Construct the payload for the Upsell Webhook
    const payload = {
      type: "upsell_quote",
      caseId: currentCaseId,
      clientName,
      contactEmail,
      items: upsellItems,
      user: user?.email,
      timestamp: new Date().toISOString(),
      hubspotIds: hubspotIds,
    };

    try {
      const response = await fetch(UPSELL_WEBHOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        confetti();
        toast.success("Quote Sent to HubSpot!");
        setShowUpsell(false);
        logAuditAction(
          "UPDATE",
          {
            collection: "onboarding_cases",
            id: currentCaseId || "",
            name: clientName || "Unknown",
          },
          { uid: user?.uid || "unknown", email: user?.email || "unknown" },
          [
            {
              field: "upsell_quote",
              oldValue: null,
              newValue: "Sent to HubSpot",
            },
          ],
          { items: upsellItems },
        );
      } else {
        const text = await response.text();
        console.error("Upsell response error:", text);
        toast.error("Failed to send quote. Check webhook connection.");
      }
    } catch (error) {
      console.error("Upsell error:", error);
      toast.error("Network error sending quote. Check console.");
    } finally {
      setIsSendingUpsell(false);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 overflow-visible">
      <div
        onClick={() => setShowUpsell(!showUpsell)}
        className="p-4 flex justify-between items-center cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div
            className={`p-2 rounded-lg ${showUpsell ? "bg-indigo-100 text-indigo-600" : "bg-gray-100 text-gray-500 dark:bg-slate-700 dark:text-gray-300"}`}
          >
            <TrendingUp size={18} />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-800 dark:text-white">
              Upsell Opportunities
            </h3>
            <p className="text-xs text-slate-500">
              Create additional quotes for HubSpot
            </p>
          </div>
        </div>
        <ChevronDown
          size={20}
          className={`text-slate-400 transition-transform ${showUpsell ? "rotate-180" : ""}`}
        />
      </div>

      {showUpsell && (
        <div className="border-t border-gray-100 dark:border-slate-700">
          {/* Upsell Add Form */}
          <div className="p-4 bg-blue-50 dark:bg-slate-900/50 border-b border-gray-100 dark:border-slate-700">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
              <div className="md:col-span-5 relative">
                <label className="text-[10px] uppercase font-bold text-gray-500">
                  Service
                </label>
                <input
                  className="w-full border p-2 rounded text-sm dark:bg-slate-800 dark:text-white"
                  placeholder="Service Name"
                  value={newUpsellItem.name}
                  onChange={(e) => {
                    setNewUpsellItem({
                      ...newUpsellItem,
                      name: e.target.value,
                    });
                    setUpsellSuggestions(
                      DEFAULT_UPSELL_SERVICES.filter((s) =>
                        s.name
                          .toLowerCase()
                          .includes(e.target.value.toLowerCase()),
                      ),
                    );
                  }}
                  onFocus={() => setUpsellSuggestions(DEFAULT_UPSELL_SERVICES)}
                  onBlur={() => setTimeout(() => setUpsellSuggestions([]), 200)}
                />
                {upsellSuggestions.length > 0 && newUpsellItem.name && (
                  <div className="absolute z-10 w-full bg-white dark:bg-slate-800 border shadow-lg max-h-40 overflow-auto rounded mt-1">
                    {upsellSuggestions.map((s, i) => (
                      <div
                        key={i}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 text-sm cursor-pointer"
                        onClick={() =>
                          setNewUpsellItem({
                            ...newUpsellItem,
                            name: s.name,
                            price: String(s.price),
                          })
                        }
                      >
                        {s.name}{" "}
                        <span className="text-gray-400 float-right">
                          €{s.price}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase font-bold text-gray-500">
                  Qty
                </label>
                <input
                  type="number"
                  className="w-full border p-2 rounded text-sm dark:bg-slate-800 dark:text-white"
                  value={newUpsellItem.quantity}
                  onChange={(e) =>
                    setNewUpsellItem({
                      ...newUpsellItem,
                      quantity: e.target.value,
                    })
                  }
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase font-bold text-gray-500">
                  Price
                </label>
                <input
                  type="number"
                  className="w-full border p-2 rounded text-sm dark:bg-slate-800 dark:text-white"
                  value={newUpsellItem.price}
                  onChange={(e) =>
                    setNewUpsellItem({
                      ...newUpsellItem,
                      price: e.target.value,
                    })
                  }
                />
              </div>
              <div className="md:col-span-3 pt-4">
                <button
                  onClick={() => {
                    if (!newUpsellItem.name) return;
                    setUpsellItems([...upsellItems, newUpsellItem]);
                    savePartial({
                      upsellItems: [...upsellItems, newUpsellItem],
                    });
                    setNewUpsellItem({
                      name: "",
                      description: "",
                      quantity: "1",
                      price: "",
                      billingFrequency: "one-time",
                    });
                  }}
                  className="w-full bg-blue-600 text-white p-2 rounded h-[38px] shadow-sm font-bold"
                >
                  Add Upsell
                </button>
              </div>
            </div>
            <textarea
              className="w-full mt-2 border p-2 rounded text-xs dark:bg-slate-800 dark:text-white resize-y"
              rows={2}
              placeholder="Optional description..."
              value={newUpsellItem.description}
              onChange={(e) =>
                setNewUpsellItem({
                  ...newUpsellItem,
                  description: e.target.value,
                })
              }
            />
          </div>

          {/* Upsell Table */}
          <table className="w-full text-sm text-left dark:text-slate-300">
            <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
              {upsellItems.map((item, idx) => {
                const isEditing = editingUpsellIndex === idx;
                return (
                  <tr
                    key={idx}
                    className="group hover:bg-slate-50 dark:hover:bg-slate-700/50"
                  >
                    <td className="px-6 py-4 w-full md:w-[40%]">
                      {isEditing ? (
                        <div className="space-y-2">
                          <input
                            className="border rounded p-1.5 w-full dark:bg-slate-900 dark:text-white font-bold"
                            value={editingUpsellItem.name}
                            onChange={(e) =>
                              setEditingUpsellItem({
                                ...editingUpsellItem,
                                name: e.target.value,
                              })
                            }
                          />
                          <textarea
                            className="border rounded p-1.5 w-full dark:bg-slate-900 dark:text-white text-xs resize-y"
                            rows={4}
                            value={editingUpsellItem.description || ""}
                            onChange={(e) =>
                              setEditingUpsellItem({
                                ...editingUpsellItem,
                                description: e.target.value,
                              })
                            }
                          />
                        </div>
                      ) : (
                        <div>
                          <div className="font-bold text-slate-800 dark:text-white">
                            {item.name}{" "}
                            <span className="text-[9px] bg-blue-50 text-blue-600 px-1 rounded uppercase">
                              {item.billingFrequency}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {item.description}
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      Qty:{" "}
                      {isEditing ? (
                        <input
                          className="border w-12 dark:bg-slate-900"
                          value={editingUpsellItem.quantity}
                          onChange={(e) =>
                            setEditingUpsellItem({
                              ...editingUpsellItem,
                              quantity: e.target.value,
                            })
                          }
                        />
                      ) : (
                        item.quantity
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      €
                      {isEditing ? (
                        <input
                          className="border w-16 dark:bg-slate-900"
                          value={editingUpsellItem.price}
                          onChange={(e) =>
                            setEditingUpsellItem({
                              ...editingUpsellItem,
                              price: e.target.value,
                            })
                          }
                        />
                      ) : (
                        parsePrice(item.price).toFixed(2)
                      )}
                    </td>
                    <td className="px-6 py-4 text-right font-bold">
                      €
                      {(
                        parsePrice(
                          isEditing ? editingUpsellItem.price : item.price,
                        ) *
                        (parseFloat(
                          isEditing
                            ? editingUpsellItem.quantity
                            : item.quantity,
                        ) || 1)
                      ).toFixed(2)}
                    </td>
                    <td className="px-4 py-4 text-right">
                      {isEditing ? (
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => {
                              updateItem(
                                upsellItems,
                                setUpsellItems,
                                editingUpsellIndex!,
                                editingUpsellItem,
                                "upsellItems",
                              );
                              setEditingUpsellIndex(null);
                            }}
                            className="text-green-600"
                          >
                            <Check size={18} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-2 justify-end opacity-0 group-hover:opacity-100">
                          <button
                            onClick={() => {
                              setEditingUpsellIndex(idx);
                              setEditingUpsellItem({
                                ...item,
                                quantity: String(item.quantity),
                                price: String(item.price),
                              });
                            }}
                            className="text-slate-400 hover:text-blue-600"
                          >
                            <Pencil size={18} />
                          </button>
                          <button
                            onClick={() =>
                              deleteItem(
                                upsellItems,
                                setUpsellItems,
                                idx,
                                "upsellItems",
                              )
                            }
                            className="text-slate-400 hover:text-red-600"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {upsellItems.length > 0 && (
            <div className="p-4 bg-blue-50/50 dark:bg-slate-900 border-t border-gray-100 dark:border-slate-700">
              <button
                onClick={handleUpsellSubmit}
                disabled={isSendingUpsell}
                className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg font-bold shadow-sm flex justify-center items-center gap-2 hover:bg-blue-700 disabled:opacity-70 disabled:cursor-not-allowed transition-all"
              >
                {isSendingUpsell ? (
                  "Sending..."
                ) : (
                  <>
                    <CreditCard size={18} /> Send Quote to HubSpot
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UpsellManager;
